import { api } from '@h2/api'

enum Locales {
  EN_US = 'en-US',
  ES_US = 'es-US',
  ES_ES = 'es-ES'
}
enum Voices {
  JOANNA = 'Joanna',
  MATTHEW = 'Matthew',
  ALEX = 'Alex',
  SAMANTHA = 'Samantha',
  JORGE = 'Jorge',
  LUCIA = 'Lucia',
  LUPE = 'Lupe',
  PEDRO = 'Pedro'
}
enum VoicesGender {
  MALE = 'Male',
  FEMALE = 'Female'
}
enum Provider {
  WEB_SPEECH_API = 'webSpeechApi',
  HTML_MEDIA_ELEMENT = 'htmlMediaElement'
}
enum Events {
  END = 'end',
  ERROR = 'error',
  READY = 'ready',
  LOADED = 'audioloaded',
  PAUSED = 'paused',
  PLAYING = 'playing'
}
interface PollySpeechMark {
  end: number
  start: number
  time: number
  type: 'word' | 'sentence'
  value: string
}
interface BoundaryUpdate {
  word: string
  startChar: number
  endChar: number
}
interface H2AudioData {
  audio: string
  text: PollySpeechMark[]
}
interface ControllerOptions {
  locale?: Locales
  name?: Voices
  gender?: VoicesGender
}
type Target = HTMLAudioElement | SpeechSynthesisUtterance
type Synthesizer = HTMLAudioElement | SpeechSynthesis

/**
 * Encapsulates API differences between HTMLAudioElement and SpeechSynthesis
 * by providing a custom interface to clients.
 */
class Controller extends EventTarget {
  protected readonly text: string
  protected readonly target: Target
  protected marks: PollySpeechMark[] = []
  protected readonly name: Voices = Voices.JOANNA
  protected readonly gender: VoicesGender = VoicesGender.FEMALE
  protected readonly locale: Locales = Locales.EN_US
  protected readonly synthesizer: Synthesizer = new Audio()
  protected readonly provider: Provider = Provider.HTML_MEDIA_ELEMENT

  constructor(text: string, provider?: Provider, options?: ControllerOptions) {
    super()

    this.text = text
    this.provider = provider ?? this.provider
    this.target = this.synthesizer as HTMLAudioElement
    this.locale = options?.locale ?? this.locale
    this.name = options?.name ?? this.name
    this.gender = options?.gender ?? this.gender

    if (this.provider === Provider.WEB_SPEECH_API) {
      const voices = window.speechSynthesis.getVoices()

      this.synthesizer = window.speechSynthesis
      this.target = new SpeechSynthesisUtterance(this.text)

      if (voices.length) {
        this.initWebSpeechVoice(voices, options?.name)
      } else {
        window.speechSynthesis.addEventListener('voiceschanged', () => {
          this.initWebSpeechVoice(window.speechSynthesis.getVoices(), options?.name)
        })
      }
    }
  }

  protected initWebSpeechVoice(voices: SpeechSynthesisVoice[], name?: Voices): void {
    const localeVoices = voices.filter(voice => voice.lang === this.locale)
    const samantha = localeVoices.find(voice => voice.name === Voices.SAMANTHA)
    const target = this.target as SpeechSynthesisUtterance

    target.voice = samantha ?? localeVoices[0] ?? voices[0]

    if (name) {
      const found = localeVoices.find(voice => voice.name === name)

      if (found) {
        target.voice = found
      }
    }
  }

  protected async attachAudioSource(): Promise<void> {
    let data: H2AudioData | null = null

    try {
      data = await api.fetch('/early/speech/read', {
        method: 'POST',
        version: 'v2',
        body: JSON.stringify({
          text: this.text,
          language: this.locale,
          name: this.name,
          gender: this.gender
        })
      })
    } catch {
      this.dispatchError()
    } finally {
      if (data && this.synthesizer instanceof HTMLAudioElement) {
        this.synthesizer.src = data.audio
        this.marks = data.text
      }
    }
  }

  protected dispatchEnd(): void {
    this.dispatchEvent(new Event(Events.END))
  }

  protected dispatchError(): void {
    this.dispatchEvent(new Event(Events.ERROR))
  }

  protected dispatchReady(): void {
    this.dispatchEvent(new Event(Events.READY))
  }

  protected dispatchPlaying(): void {
    this.dispatchEvent(new Event(Events.PLAYING))
  }

  protected dispatchPaused(): void {
    this.dispatchEvent(new Event(Events.PAUSED))
  }

  protected async playHtmlAudio(): Promise<void> {
    const audio = this.synthesizer as HTMLAudioElement

    if (!audio.src) {
      await this.attachAudioSource()
    }

    try {
      await audio.play()
    } catch {
      this.dispatchError()
    }
  }

  protected getSpeechMarkForAudioTime(time: number): PollySpeechMark {
    const length = this.marks.length
    let bestMatch = this.marks[0]
    let found = false
    let i = 1

    while (i < length && !found) {
      if (this.marks[i].time <= time) {
        bestMatch = this.marks[i]
      } else {
        found = true
      }

      i++
    }

    return bestMatch
  }

  protected getBoundaryWordCharLength(startIndex: number): number {
    const match = this.text.substring(startIndex).match(/.+?\b/)

    return match ? match[0].length : 0
  }

  /**
   * Provide explicit initialization and binding of event listeners
   * so clients can attach their listeners at a time of their choosing,
   * for example inside of a useEffect which may run after an instance
   * of this class has dispatched certain events.
   */
  init(onBoundaryChange?: (boundary: BoundaryUpdate) => void): void {
    if (this.target instanceof SpeechSynthesisUtterance) {
      this.target.pitch = 1
      this.target.rate = 0.9
      this.target.volume = 1
      this.target.addEventListener('end', this.dispatchEnd.bind(this))
      this.target.addEventListener('start', this.dispatchPlaying.bind(this))
      this.target.addEventListener('resume', this.dispatchPlaying.bind(this))
      this.target.addEventListener('pause', this.dispatchPaused.bind(this))

      if (onBoundaryChange) {
        this.target.addEventListener('boundary', evt => {
          const { charIndex: startChar } = evt
          // Safari does not include evt.charLength
          const charLength = evt.charLength ?? this.getBoundaryWordCharLength(startChar)
          const endChar = startChar + charLength
          const word = this.text.substring(startChar, endChar)

          if (word) {
            onBoundaryChange({ word, startChar, endChar })
          }
        })
      }

      this.dispatchReady()
    }

    if (this.target instanceof HTMLAudioElement) {
      const handleCanPlay = () => {
        this.dispatchReady()
        /**
         * Only dispatch ready event once per instance.
         * The 'canplay' event will fire multiple times throughout
         * the instances lifecycle depending on user action.
         *
         * We want to use this as an event to initialize the controls.
         */
        this.target.removeEventListener('canplay', handleCanPlay)
      }

      this.target.addEventListener('ended', this.dispatchEnd.bind(this))
      this.target.addEventListener('canplay', handleCanPlay)

      if (onBoundaryChange) {
        this.target.addEventListener('timeupdate', () => {
          // Polly Speech Marks use milliseconds
          const currentTime = (this.target as HTMLAudioElement).currentTime * 1000
          const mark = this.getSpeechMarkForAudioTime(currentTime)

          if (mark) {
            onBoundaryChange({
              word: mark.value,
              startChar: mark.start,
              endChar: mark.end
            })
          }
        })
      }
    }

    // Attach listeners for events with a common name
    this.target.addEventListener('error', this.dispatchError.bind(this))
  }

  play(): void {
    if (this.synthesizer instanceof HTMLAudioElement) {
      this.playHtmlAudio()
    } else {
      this.synthesizer.speak(this.target as SpeechSynthesisUtterance)
    }
  }

  pause(): void {
    this.synthesizer.pause()
  }

  resume(): void {
    if (this.synthesizer instanceof HTMLAudioElement) {
      this.playHtmlAudio()
    } else {
      this.synthesizer.resume()
    }
  }

  reset(): void {
    if (this.synthesizer instanceof HTMLAudioElement) {
      this.synthesizer.load()
      this.playHtmlAudio()
    } else {
      this.synthesizer.cancel()
      this.synthesizer.speak(this.target as SpeechSynthesisUtterance)
    }
  }

  clear(): void {
    if (this.synthesizer instanceof HTMLAudioElement) {
      this.synthesizer.pause()
      this.synthesizer.currentTime = 0
    } else {
      this.synthesizer.cancel()
    }
  }
}

export { Controller, Provider, Events, Locales, Voices, VoicesGender }
export type { ControllerOptions, BoundaryUpdate }
